/**
 * One open production in the playout view: load it, keep it live, command it
 * — the non-React half of the operator screen (PLAN §5.2, §5.3, §6.6, D3).
 *
 *   load      `GET /productions/{id}` (the config snapshot, D22) and the rows
 *             of the rundown — or of every rundown of the playlist, in running
 *             order — it runs; `GET …/playout` for the state now
 *   live      the playout stream (`@acp/api-client/playoutWatch.js`):
 *             `playout.state` full and delta frames, a gap re-read over REST,
 *             `device.state`; and each rundown's row stream (the rundown watch
 *             client, without presence), so an edit shows here as it lands —
 *             and D19's "Update on air?" can see the on-air part's definition
 *             move away from its instance
 *   command   `POST …/commands` with a minted `commandId`. **Never
 *             optimistic** (§6.6: commands are imperative): a command is
 *             pending until its ack or nack, and the state it changed arrives
 *             on the stream like anyone else's. A nack is reported with its
 *             reason; an ack whose `seq` the stream has not delivered soon
 *             after (the socket is down) re-reads the state
 *   primary   the playout socket's state goes to the connectivity manager
 *             (`reportSocket`); a primary change reloads from the new primary
 *
 * Every dependency is a port (CLAUDE.md → explicit dependency injection).
 */
import { allPages, type ApiClient } from '@acp/api-client/client.js';
import { watchPlayout, type PlayoutWatchEvent, type PlayoutWatchHandle } from '@acp/api-client/playoutWatch.js';
import type { SocketFactory, Timers } from '@acp/api-client/ports.js';
import { listAllRows } from '@acp/api-client/rows.js';
import { watchRundown, type WatchHandle } from '@acp/api-client/watchClient.js';
import type { CommandResult, DeviceStatus, PlayoutCommandType, PlayoutState } from '@acp/api-contract/playout.js';
import type { Production } from '@acp/api-contract/productions.js';
import { compareByOrd } from '@acp/domain/ordering.js';
import type { Row } from '@acp/domain/row.js';
import { parseStudioConfig, type StudioConfig } from '@acp/engine/studioConfig.js';
import type { SocketState } from '../connectivity/state.js';
import { createRowStore, type RowStore } from '../lib/editor/rowStore.js';
import type { CommandInput } from '../lib/playout/commands.js';
import type { RundownSource } from '../lib/playout/viewModel.js';

/** How long an acked command's `seq` may take to arrive on the stream before the state is re-read over REST. */
export const ACK_RESYNC_MS = 1_500;


/**
 * A command not yet reflected on screen: unanswered, or acked (`ackedSeq`)
 * while the state including it has not arrived. Keeping an acked take pending
 * until its state lands is what stops a second press from passing the take
 * guard on the previous take's `lastTakeTime` (found in e2e: an F12 pressed
 * between the ack and the state was sent, and nacked take-too-soon).
 */
export type PendingCommand = { readonly commandId: string; readonly type: PlayoutCommandType; readonly sentAt: number; readonly ackedSeq: number | null };

export type CommandOutcome = {
  readonly commandId: string;
  readonly type: PlayoutCommandType;
  readonly accepted: boolean;
  /** The nack's reason, or `not-delivered` when no answer came (the command may or may not have run). */
  readonly reason: string | null;
  readonly message: string | null;
  readonly at: number;
};

/** The live stream: `production-lost` is the node's link to the production process down (`playout.link`), the socket fine. */
export type PlayoutConnection = 'connecting' | 'live' | 'reconnecting' | 'production-lost';

export type LoadedRundown = { readonly rundownId: string; readonly name: string; readonly store: RowStore };

export type PlayoutSnapshot = {
  readonly status: 'loading' | 'ready' | 'failed';
  readonly error: string | null;
  readonly production: Production | null;
  /** The config snapshot, parsed with the engine's schema; null until loaded or when it fails. */
  readonly config: StudioConfig | null;
  readonly configError: string | null;
  /** The rundowns in running order, their trees current (a new array whenever any row changed). */
  readonly rundowns: readonly RundownSource[];
  readonly state: PlayoutState | null;
  /** The browser's clock when `state` arrived (countdowns advance from it, `lib/playout/clock.ts`). */
  readonly stateAt: number;
  /** Why there is no live state (the production process is unreachable, the stream was refused). */
  readonly stateError: string | null;
  readonly devices: readonly DeviceStatus[];
  readonly connection: PlayoutConnection;
  readonly retryInMs: number | null;
  readonly pending: readonly PendingCommand[];
  readonly lastOutcome: CommandOutcome | null;
};

export type PlayoutNotice = { readonly level: 'error' | 'info'; readonly message: string };

export type PlayoutSessionDeps = {
  readonly api: ApiClient;
  readonly wsUrl: () => string;
  readonly reportSocket: (state: SocketState) => void;
  readonly tenantId: string;
  readonly productionId: string;
  readonly token: () => Promise<string>;
  readonly openSocket: SocketFactory;
  readonly timers: Timers;
  readonly nowMs: () => number;
  /** Mints a command id (`commandIdSchema`: `[A-Za-z0-9._:-]{1,128}`). */
  readonly newCommandId: () => string;
  readonly notify: (notice: PlayoutNotice) => void;
};

export type PlayoutSession = {
  readonly snapshot: () => PlayoutSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  /**
   * Send a command; resolves with its answer (null: not delivered, or a take
   * refused here because another is still pending). Never throws.
   */
  readonly send: (command: CommandInput) => Promise<CommandResult | null>;
  /** Load again after a failed load, or restart a playout stream that gave up. */
  readonly retry: () => void;
  readonly start: () => void;
  readonly stop: () => void;
  readonly setPrimary: (primaryId: string) => void;
};

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function nameOf(row: Row): string {
  const name = row.f['name'];
  return typeof name === 'string' && name !== '' ? name : '(unnamed rundown)';
}

const INITIAL: PlayoutSnapshot = {
  status: 'loading',
  error: null,
  production: null,
  config: null,
  configError: null,
  rundowns: [],
  state: null,
  stateAt: 0,
  stateError: null,
  devices: [],
  connection: 'connecting',
  retryInMs: null,
  pending: [],
  lastOutcome: null,
};

export function createPlayoutSession(deps: PlayoutSessionDeps): PlayoutSession {
  const listeners = new Set<() => void>();
  let snap: PlayoutSnapshot = INITIAL;
  let loaded: LoadedRundown[] = [];
  let playoutWatch: PlayoutWatchHandle | null = null;
  let rowWatches: WatchHandle[] = [];
  let stopped = true;
  /** Bumped by every start, stop and primary change: work from an older generation drops its result. */
  let generation = 0;
  let primaryId: string | null = null;
  /** The playout stream ended for good (no token): nothing reconnects until `retry`. */
  let streamEnded = false;

  const update = (patch: Partial<PlayoutSnapshot>): void => {
    snap = { ...snap, ...patch };
    for (const listener of listeners) listener();
  };

  const sources = (): RundownSource[] => loaded.map((r) => ({ rundownId: r.rundownId, name: r.name, tree: r.store.tree }));

  /** Adopt a state. From the stream (full, delta, resync) it is authoritative; a REST read only if not older. */
  const adopt = (state: PlayoutState, fromStream: boolean): void => {
    if (!fromStream && snap.state !== null && state.seq < snap.state.seq) return;
    const pending = snap.pending.filter((p) => p.ackedSeq === null || p.ackedSeq > state.seq);
    update({ state, stateAt: deps.nowMs(), stateError: null, devices: state.devices, pending });
  };

  const readState = async (gen: number): Promise<void> => {
    try {
      const res = await deps.api.call('getPlayout', { params: { productionId: deps.productionId }, query: null, body: null });
      if (gen === generation && !stopped) adopt(res.state, false);
    } catch (err) {
      if (gen === generation && !stopped && snap.state === null) update({ stateError: messageOf(err) });
    }
  };

  const onPlayout = (event: PlayoutWatchEvent, gen: number): void => {
    if (stopped || gen !== generation) return;
    switch (event.type) {
      case 'state':
        deps.reportSocket('open');
        if (snap.connection !== 'live') update({ connection: 'live', retryInMs: null });
        adopt(event.state, true);
        return;
      case 'devices':
        update({ devices: event.devices });
        return;
      case 'link':
        // Down: the state and devices held are the last the process sent; its full state (then `up`) ends that.
        if (event.status === 'down') update({ connection: 'production-lost' });
        else if (snap.connection === 'production-lost') update({ connection: 'live' });
        return;
      case 'nack':
        update({ stateError: event.message.payload.message });
        deps.notify({ level: 'error', message: `Live playout refused: ${event.message.payload.message}` });
        return;
      case 'disconnected':
        deps.reportSocket('connecting');
        update({ connection: 'reconnecting', retryInMs: event.retryInMs });
        return;
      case 'warning':
        // A gap re-read, an unparseable frame: the watch client handles them; nothing the operator must act on.
        return;
    }
  };

  const startPlayoutWatch = (gen: number): void => {
    streamEnded = false;
    playoutWatch?.stop();
    deps.reportSocket('connecting');
    const handle = watchPlayout({
      wsUrl: deps.wsUrl(),
      productionId: deps.productionId,
      tenantId: deps.tenantId,
      token: deps.token,
      openSocket: deps.openSocket,
      timers: deps.timers,
      nowMs: deps.nowMs,
      fetchState: async () => (await deps.api.call('getPlayout', { params: { productionId: deps.productionId }, query: null, body: null })).state,
      onEvent: (event) => onPlayout(event, gen),
    });
    playoutWatch = handle;
    handle.done.catch((err: unknown) => {
      if (stopped || gen !== generation) return;
      streamEnded = true;
      deps.reportSocket('closed');
      update({ connection: 'reconnecting', stateError: `live playout stopped: ${messageOf(err)}` });
    });
  };

  const startRowWatch = (rundown: LoadedRundown, cursor: string, gen: number): WatchHandle => {
    const handle = watchRundown({
      wsUrl: deps.wsUrl(),
      rundownId: rundown.rundownId,
      tenantId: deps.tenantId,
      token: deps.token,
      openSocket: deps.openSocket,
      timers: deps.timers,
      nowMs: deps.nowMs,
      catchUp: (from) => listAllRows(deps.api, rundown.rundownId, { changedSince: from }),
      since: cursor,
      presence: null,
      onEvent: (event) => {
        if (stopped || gen !== generation || event.type !== 'row') return;
        if (rundown.store.receive([event.row], false).length > 0) update({ rundowns: sources() });
      },
    });
    // A fatal end (no token) is the playout stream's to report — it fails the same way; never an unhandled rejection.
    handle.done.catch(() => undefined);
    return handle;
  };

  /** The rundowns a production runs, in running order. */
  const rundownIdsOf = async (production: Production): Promise<string[]> => {
    if (production.rundownId !== null) return [production.rundownId];
    const playlistId = production.playlistId;
    if (playlistId === null) return [];
    const heads = await allPages(async (pageToken) => {
      const page = await deps.api.call('listRundowns', { params: null, query: pageToken === undefined ? { playlistId } : { playlistId, pageToken }, body: null });
      return { items: page.rundowns, nextPageToken: page.nextPageToken };
    });
    return heads.sort((a, b) => compareByOrd({ id: a.rowId, ord: a.ord }, { id: b.rowId, ord: b.ord })).map((h) => h.rowId);
  };

  const loadRundown = async (rundownId: string): Promise<{ rundown: LoadedRundown; cursor: string }> => {
    const head = await deps.api.call('getRundown', { params: { rundownId }, query: null, body: null });
    const listing = await listAllRows(deps.api, rundownId, {});
    const store = createRowStore();
    store.receive([head.rundown, ...listing.rows], true);
    return { rundown: { rundownId, name: nameOf(head.rundown), store }, cursor: listing.cursor ?? head.cursor };
  };

  const load = async (): Promise<void> => {
    const gen = generation;
    update({ status: 'loading', error: null });
    try {
      const { production } = await deps.api.call('getProduction', { params: { productionId: deps.productionId }, query: null, body: null });
      const parsed = parseStudioConfig(production.config);
      const results = await Promise.all((await rundownIdsOf(production)).map(loadRundown));
      if (stopped || gen !== generation) return;
      loaded = results.map((r) => r.rundown);
      update({
        status: 'ready',
        production,
        config: parsed.ok ? parsed.config : null,
        configError: parsed.ok ? null : parsed.error,
        rundowns: sources(),
      });
      for (const w of rowWatches) w.stop();
      rowWatches = results.map((r) => startRowWatch(r.rundown, r.cursor, gen));
      startPlayoutWatch(gen);
      void readState(gen);
    } catch (err) {
      if (!stopped && gen === generation) update({ status: 'failed', error: messageOf(err) });
    }
  };

  const stopWatches = (): void => {
    playoutWatch?.stop();
    playoutWatch = null;
    for (const w of rowWatches) w.stop();
    rowWatches = [];
  };

  const send = async (input: CommandInput): Promise<CommandResult | null> => {
    // The double-take guard's last line, on the session's own state rather than a rendered copy of it: two key
    // presses inside one frame both see the render before the first take was pending (found in e2e).
    if (input.type === 'take' && snap.pending.some((p) => p.type === 'take')) return null;
    const commandId = deps.newCommandId();
    const gen = generation;
    const entry: PendingCommand = { commandId, type: input.type, sentAt: deps.nowMs(), ackedSeq: null };
    update({ pending: [...snap.pending, entry] });
    const drop = (): void => update({ pending: snap.pending.filter((p) => p.commandId !== commandId) });
    const settle = (outcome: CommandOutcome): void => {
      update({ pending: snap.pending.filter((p) => p.commandId !== commandId), lastOutcome: outcome });
    };
    try {
      const result = await deps.api.call('sendPlayoutCommand', { params: { productionId: deps.productionId }, query: null, body: { ...input, commandId } });
      if (result.accepted) {
        const outcome = { commandId, type: input.type, accepted: true, reason: null, message: null, at: deps.nowMs() };
        if ((snap.state?.seq ?? -1) >= result.seq) settle(outcome);
        else update({ pending: snap.pending.map((p) => (p.commandId === commandId ? { ...p, ackedSeq: result.seq } : p)), lastOutcome: outcome });
        // The stream normally brings the new state first; if it has not (a dead socket), read it — and stop waiting either way.
        deps.timers.after(ACK_RESYNC_MS, () => {
          if (stopped) return;
          if (gen !== generation || (snap.state?.seq ?? -1) >= result.seq) drop();
          else void readState(gen).then(drop);
        });
      } else {
        settle({ commandId, type: input.type, accepted: false, reason: result.reason, message: result.message, at: deps.nowMs() });
        deps.notify({ level: 'error', message: `${input.type} refused (${result.reason}): ${result.message}` });
      }
      return result;
    } catch (err) {
      settle({ commandId, type: input.type, accepted: false, reason: 'not-delivered', message: messageOf(err), at: deps.nowMs() });
      deps.notify({ level: 'error', message: `${input.type} not delivered — it may or may not have run: ${messageOf(err)}` });
      return null;
    }
  };

  return {
    snapshot: () => snap,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    send,
    retry: () => {
      if (stopped) return;
      if (snap.status === 'failed') void load();
      else if (streamEnded) {
        // The stream gave up (a token that could not be had): start it again, and read the state now.
        update({ stateError: null });
        startPlayoutWatch(generation);
        void readState(generation);
      } else if (snap.stateError !== null) void readState(generation);
    },
    start: () => {
      if (!stopped) return;
      stopped = false;
      generation += 1;
      void load();
    },
    stop: () => {
      if (stopped) return;
      stopped = true;
      generation += 1;
      stopWatches();
      deps.reportSocket('unknown');
      update({ connection: 'connecting' });
    },
    setPrimary: (id) => {
      const changed = primaryId !== null && primaryId !== id;
      primaryId = id;
      if (!changed || stopped) return;
      generation += 1;
      stopWatches();
      // Another host: its seq and row versions are its own — start from its full state.
      // Acked commands waiting for their state: the new host's seq is its own, so stop waiting.
      update({ connection: 'connecting', retryInMs: null, state: null, pending: snap.pending.filter((p) => p.ackedSeq === null) });
      void load();
    },
  };
}
